import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

/**
 * `/accounting/opening` — what the company walked into Hub with.
 *
 * The figures come from whoever kept the books before, so the page is
 * built around pasting them in rather than typing them: an accountant
 * exports a trial balance and drops it here. Posting happens once, and
 * the page says so plainly — a second posting would double everything
 * the year started with.
 */

const searchSchema = z.object({ year: z.number().optional() });

type Row = {
  id: string;
  accountCode: string;
  debit: string;
  credit: string;
  note: string | null;
};

type Response = {
  year: number;
  rows: Row[];
  totals: { debit: string; credit: string; difference: string; balanced: boolean };
  posted: { id: string; number: number; bookedOn: string } | null;
};

export const Route = createFileRoute("/_app/accounting/opening")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  loader: async ({ deps }) => {
    try {
      return await api.get<Response>(
        `/api/accounting/opening-balances${deps.year ? `?year=${deps.year}` : ""}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: OpeningPage,
});

function money(amount: string): string {
  return Number(amount).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function OpeningPage() {
  const data = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const [text, setText] = useState("");
  const [details, setDetails] = useState<string[]>([]);

  const save = useApiMutation(
    () => api.put("/api/accounting/opening-balances", { year: data.year, text }),
    {
      onSuccess: () => {
        setText("");
        setDetails([]);
        router.invalidate();
      },
      errorMessage: (err) => {
        // The server names the lines it couldn't read; showing only
        // "didn't work" would leave the accountant hunting for which one.
        const detail = err instanceof ApiError ? (err.detail as { details?: string[] }) : null;
        setDetails(detail?.details ?? []);
        return "Couldn't read the opening balances.";
      },
    },
  );

  const post = useApiMutation(
    () => api.post("/api/accounting/opening-balances/post", { year: data.year }),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't post them." },
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Opening balances</h1>
          <p className="text-muted-foreground text-sm">
            The state the company arrived with. Until these are posted, the balance sheet doesn't
            add up and the receivables ledger says nothing about anything older than the migration.
          </p>
        </div>
        <label className="text-sm">
          <span className="text-muted-foreground text-xs">Year</span>
          <input
            type="number"
            defaultValue={data.year}
            className="mt-1 block h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) =>
              navigate({ to: "/accounting/opening", search: { year: Number(e.target.value) } })
            }
          />
        </label>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">
              {data.posted ? "Posted" : data.rows.length > 0 ? "Ready to post" : "Nothing yet"}
            </CardTitle>
            <CardDescription>
              Debit {money(data.totals.debit)} · credit {money(data.totals.credit)}
              {!data.totals.balanced && ` · difference ${money(data.totals.difference)}`}
            </CardDescription>
          </div>
          {data.posted ? (
            <Badge variant="success">journal entry no. {data.posted.number}</Badge>
          ) : (
            <Button
              disabled={post.pending || data.rows.length === 0 || !data.totals.balanced}
              onClick={() => post.mutate()}
            >
              {post.pending ? "Posting…" : "Post to the journal"}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!data.totals.balanced && data.rows.length > 0 && (
            <p className="text-destructive text-sm">
              Debit and credit differ by {money(data.totals.difference)}. An entry that doesn't add
              up can't be posted — the figures need fixing at the source.
            </p>
          )}
          {post.error && <p className="text-destructive text-sm">{post.error}</p>}
          {data.posted && (
            <p className="text-muted-foreground text-sm">
              Posted once, on purpose. A correction from here on is an ordinary journal entry — the
              journal isn't edited.
            </p>
          )}

          {data.rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Account</th>
                  <th className="py-1 pr-4 text-right font-medium">Debit</th>
                  <th className="py-1 text-right font-medium">Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="py-1 pr-4 tabular-nums">{row.accountCode}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {Number(row.debit) > 0 ? money(row.debit) : "—"}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {Number(row.credit) > 0 ? money(row.credit) : "—"}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold">
                  <td className="py-1 pr-4">Total</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(data.totals.debit)}</td>
                  <td className="py-1 text-right tabular-nums">{money(data.totals.credit)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {!data.posted && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Paste the trial balance</CardTitle>
            <CardDescription>
              One account per line: <code>account;debit;credit</code>. Replaces whatever is here —
              until it's posted, nothing is set in stone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              value={text}
              rows={8}
              placeholder={"311;125000;0\n321;0;48000\n221;310000;0"}
              className="w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                disabled={save.pending || text.trim().length === 0}
                onClick={() => save.mutate()}
              >
                {save.pending ? "Reading…" : "Read and save"}
              </Button>
              {save.error && <p className="text-destructive text-sm">{save.error}</p>}
            </div>
            {details.length > 0 && (
              <ul className="space-y-1 text-destructive text-xs">
                {details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
